import React from 'react';

/**
 * TeamSelectionPanel - Reusable component for team selection UI
 *
 * Props:
 * - game: The game document
 * - userId: Current user's ID
 * - onSelectTeam: (team: 'teamA' | 'teamB') => void
 * - onConfirm: () => void
 * - showConfirmButton: boolean (default true)
 * - disabled: boolean (default false)
 * - compact: boolean (default false) - for smaller display in lobby
 */
const TeamSelectionPanel = ({
  game,
  userId,
  onSelectTeam,
  onConfirm,
  showConfirmButton = true,
  disabled = false,
  compact = false
}) => {
  const players = game?.players || {};
  const confirmations = game?.playerTeamConfirmations || {};

  // Get current user's selection
  const myPlayer = players[userId];
  const myTeam = myPlayer?.team;
  const isConfirmed = confirmations[userId] === true;

  // Separate players by team
  const teamAPlayers = Object.entries(players)
    .filter(([id, p]) => p.team === 'teamA' && p.isActive !== false)
    .map(([id, p]) => ({ id, ...p, confirmed: confirmations[id] === true }));

  const teamBPlayers = Object.entries(players)
    .filter(([id, p]) => p.team === 'teamB' && p.isActive !== false)
    .map(([id, p]) => ({ id, ...p, confirmed: confirmations[id] === true }));

  const unassignedPlayers = Object.entries(players)
    .filter(([id, p]) => !p.team && p.isActive !== false)
    .map(([id, p]) => ({ id, ...p }));

  const canConfirm = myTeam && !isConfirmed && !disabled;

  const containerStyle = compact ? {
    background: 'var(--bg-tertiary)',
    borderRadius: 'var(--radius-lg)',
    padding: '1rem'
  } : {
    background: 'white',
    borderRadius: 'var(--radius-xl)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow-md)'
  };

  const renderPlayerList = (players, teamColor, emptyMessage) => (
    <div style={{ minHeight: compact ? '60px' : '100px' }}>
      {players.length === 0 ? (
        <div style={{
          color: 'var(--text-tertiary)',
          fontSize: compact ? '0.75rem' : '0.875rem',
          fontStyle: 'italic',
          padding: '0.5rem'
        }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {players.map((player) => (
            <div
              key={player.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: compact ? '0.375rem 0.5rem' : '0.5rem 0.75rem',
                background: player.id === userId ? `${teamColor}15` : 'var(--bg-secondary)',
                borderRadius: 'var(--radius)',
                border: player.id === userId ? `2px solid ${teamColor}` : 'none',
                fontSize: compact ? '0.875rem' : '1rem'
              }}
            >
              <div
                className="avatar"
                style={{
                  width: compact ? '24px' : '32px',
                  height: compact ? '24px' : '32px',
                  fontSize: compact ? '0.75rem' : '0.875rem',
                  background: teamColor
                }}
              >
                {player.name?.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: player.id === userId ? 600 : 400 }}>
                  {player.name}
                  {player.id === userId && ' (You)'}
                </span>
              </div>
              {player.confirmed && (
                <span style={{
                  fontSize: compact ? '0.625rem' : '0.75rem',
                  background: '#d1fae5',
                  color: '#065f46',
                  padding: '0.125rem 0.375rem',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 600
                }}>
                  Ready
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={containerStyle}>
      {!compact && (
        <h3 style={{ marginBottom: '1rem', textAlign: 'center' }}>
          Choose Your Team
        </h3>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: compact ? '0.75rem' : '1rem',
        marginBottom: compact ? '0.75rem' : '1rem'
      }}>
        {/* Team A */}
        <div style={{
          background: myTeam === 'teamA' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)',
          padding: compact ? '0.75rem' : '1rem',
          border: myTeam === 'teamA' ? '2px solid var(--primary)' : '2px solid transparent',
          transition: 'all 0.2s ease'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: compact ? '0.5rem' : '0.75rem'
          }}>
            <div style={{
              fontWeight: 700,
              color: 'var(--primary)',
              fontSize: compact ? '0.875rem' : '1rem'
            }}>
              Team A
            </div>
            <div style={{
              fontSize: compact ? '0.75rem' : '0.875rem',
              color: 'var(--text-secondary)',
              fontWeight: 600
            }}>
              {teamAPlayers.length} player{teamAPlayers.length !== 1 ? 's' : ''}
            </div>
          </div>

          {renderPlayerList(teamAPlayers, 'var(--primary)', 'No players yet')}

          {!disabled && (
            <button
              onClick={() => onSelectTeam('teamA')}
              className={`btn btn-sm btn-full ${myTeam === 'teamA' ? 'btn-primary' : 'btn-outline'}`}
              style={{
                marginTop: compact ? '0.5rem' : '0.75rem'
              }}
            >
              {myTeam === 'teamA' ? (isConfirmed ? 'Selected (tap to switch)' : 'Selected') : 'Join Team A'}
            </button>
          )}
        </div>

        {/* Team B */}
        <div style={{
          background: myTeam === 'teamB' ? 'rgba(236, 72, 153, 0.1)' : 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)',
          padding: compact ? '0.75rem' : '1rem',
          border: myTeam === 'teamB' ? '2px solid var(--secondary)' : '2px solid transparent',
          transition: 'all 0.2s ease'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: compact ? '0.5rem' : '0.75rem'
          }}>
            <div style={{
              fontWeight: 700,
              color: 'var(--secondary)',
              fontSize: compact ? '0.875rem' : '1rem'
            }}>
              Team B
            </div>
            <div style={{
              fontSize: compact ? '0.75rem' : '0.875rem',
              color: 'var(--text-secondary)',
              fontWeight: 600
            }}>
              {teamBPlayers.length} player{teamBPlayers.length !== 1 ? 's' : ''}
            </div>
          </div>

          {renderPlayerList(teamBPlayers, 'var(--secondary)', 'No players yet')}

          {!disabled && (
            <button
              onClick={() => onSelectTeam('teamB')}
              className={`btn btn-sm btn-full ${myTeam === 'teamB' ? '' : 'btn-outline'}`}
              style={{
                marginTop: compact ? '0.5rem' : '0.75rem',
                background: myTeam === 'teamB' ? 'var(--secondary)' : undefined,
                color: myTeam === 'teamB' ? 'white' : undefined
              }}
            >
              {myTeam === 'teamB' ? (isConfirmed ? 'Selected (tap to switch)' : 'Selected') : 'Join Team B'}
            </button>
          )}
        </div>
      </div>

      {/* Unassigned players */}
      {unassignedPlayers.length > 0 && (
        <div style={{
          marginBottom: compact ? '0.75rem' : '1rem',
          padding: compact ? '0.5rem' : '0.75rem',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius)',
          fontSize: compact ? '0.75rem' : '0.875rem'
        }}>
          <div style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Waiting to choose:
          </div>
          <div style={{ color: 'var(--text-primary)' }}>
            {unassignedPlayers.map(p => p.name).join(', ')}
          </div>
        </div>
      )}

      {/* Confirm button */}
      {showConfirmButton && !disabled && (
        <div style={{ textAlign: 'center' }}>
          {isConfirmed ? (
            <div style={{
              padding: compact ? '0.5rem' : '0.75rem',
              background: '#d1fae5',
              color: '#065f46',
              borderRadius: 'var(--radius)',
              fontWeight: 600,
              fontSize: compact ? '0.875rem' : '1rem'
            }}>
              You're ready! Waiting for others...
            </div>
          ) : (
            <button
              onClick={onConfirm}
              disabled={!canConfirm}
              className="btn btn-primary btn-lg"
              style={{
                width: compact ? '100%' : 'auto',
                minWidth: compact ? undefined : '200px'
              }}
            >
              {!myTeam ? 'Select a team first' : 'Confirm Selection'}
            </button>
          )}
        </div>
      )}

      {/* Status message for disabled state */}
      {disabled && (
        <div style={{
          textAlign: 'center',
          padding: '0.75rem',
          color: 'var(--text-secondary)',
          fontSize: '0.875rem'
        }}>
          Team selection is locked during the round
        </div>
      )}
    </div>
  );
};

export default TeamSelectionPanel;
